import { vtkReader, vtkWriter, vtkClass } from '@/src/types/vtk-types';
import { readFileAsArrayBuffer, readFileAsUTF8Text } from '@/src/io';
import vtkDataSet from '@kitware/vtk.js/Common/DataModel/DataSet';

export async function readFile(
  file: File,
  vtkReaderClass: vtkClass,
  asBinary = true
) {
  const reader: vtkReader = vtkReaderClass.newInstance() as vtkReader;
  if (asBinary) {
    const buffer = await readFileAsArrayBuffer(file);
    reader.parseAsArrayBuffer(buffer);
  } else {
    const buffer = await readFileAsUTF8Text(file);
    reader.parseAsText(buffer);
  }
  return reader.getOutputData();
}

export async function writeData(vtkWriterClass: vtkClass, data: vtkDataSet) {
  const writer: vtkWriter = vtkWriterClass.newInstance() as vtkWriter;

  return writer.write(data);
}

export interface StateObject {
  vtkClass: string;
  [attrName: string]: unknown;
}

// vtk.js DataArray.getState() calls Array.from() on typed arrays, converting
// them to plain JS number arrays (~8x the memory). For large datasets (big
// STL meshes, high-res volumes) that transient spike can OOM-crash the tab.
// This helper temporarily swaps each array's data with a small sentinel
// before getState(), then injects the original TypedArrays into the
// resulting state. Structured clone (postMessage) handles TypedArrays
// efficiently, and vtk() reconstruction accepts them in DataArray.extend(),
// which re-derives dataType/size from the actual values.
const SENTINEL = 0x7f56544b;
// Divisible by any numberOfComponents (1-4) so component counts survive
// the setData() swap.
const SENTINEL_LENGTH = 12;

function collectDataArrays(dataSet: vtkDataSet) {
  const ds = dataSet as any;
  const arrays: any[] = [];
  [ds.getPointData?.(), ds.getCellData?.(), ds.getFieldData?.()].forEach(
    (attrs: any) => {
      attrs?.getArrays?.()?.forEach((arr: any) => arr && arrays.push(arr));
    }
  );
  // Only polydata: vtkImageData.getPoints() would compute a dense point
  // array, which is exactly what we're trying to avoid.
  if (ds.isA?.('vtkPolyData')) {
    [
      ds.getPoints?.(),
      ds.getVerts?.(),
      ds.getLines?.(),
      ds.getPolys?.(),
      ds.getStrips?.(),
    ].forEach((arr: any) => arr && arrays.push(arr));
  }
  return arrays;
}

export const getStateWithTypedArrays = (dataSet: vtkDataSet): StateObject => {
  const arrays = collectDataArrays(dataSet);
  const typedArrays = arrays.map((arr: any) => arr.getData());

  arrays.forEach((arr: any, i: number) => {
    const sentinel = new Uint32Array(SENTINEL_LENGTH);
    sentinel[0] = SENTINEL;
    sentinel[1] = i;
    arr.setData(sentinel);
  });

  let state: any;
  try {
    state = dataSet.getState();
  } finally {
    arrays.forEach((arr: any, i: number) => arr.setData(typedArrays[i]));
  }

  const inject = (node: any) => {
    if (!node || typeof node !== 'object' || ArrayBuffer.isView(node)) return;
    const { values } = node;
    if (
      Array.isArray(values) &&
      values.length === SENTINEL_LENGTH &&
      values[0] === SENTINEL
    ) {
      const original = typedArrays[values[1]];
      node.values = original;
      node.size = original.length;
      return;
    }
    Object.values(node).forEach(inject);
  };
  inject(state);

  return state;
};

/** Unique ArrayBuffers backing the TypedArrays in a serialized state. */
export const collectStateBuffers = (state: unknown): ArrayBuffer[] => {
  const buffers = new Set<ArrayBuffer>();
  const walk = (node: any) => {
    if (!node || typeof node !== 'object') return;
    if (ArrayBuffer.isView(node)) {
      buffers.add(node.buffer as ArrayBuffer);
      return;
    }
    Object.values(node).forEach(walk);
  };
  walk(state);
  return [...buffers];
};
